
import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";

import Swal from "sweetalert2";
import Layout from "../../../Layout";
import SwitchBar from "../../../components/Pages/SwitchBar";
import HeaderBread from "../../../components/HeaderBread";
import CustomizedTable from "../../../components/UI/CustomizedTable";
import { useDataLayerValue } from "../../../DataLayer/DataLayer";
import { getVessels } from "../../../utils/ApiCalls/SpecialPage";
import { checkPermission } from "../../../utils/Validation/checkPermission";
import { useLocation, useNavigate } from "react-router-dom";
import Api from "../../../utils/Api";
import Logs from "../../../components/Logs";

const navigation = [
    { title: "Home", route: "/" },
    { title: "Itinerary List", route: "/shore/mobile-masters/packages" },
];

const ItineraryPackages = () => {
    const [{ permission }, dispatch] = useDataLayerValue();
    const navigate = useNavigate();
    const location = useLocation();

    const firstLoad = useRef(true);
    const prevVesselRef = useRef(null);

    const [vesselOptions, setVesselOptions] = useState([]);
    const [vesselId, setVesselId] = useState(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [count, setCount] = useState(0);
    const [columns, setColumns] = useState([]);
    // Add tab state for SwitchBar
    const [active, setActive] = useState("listing");

    const [page, setPage] = useState(
        () => parseInt(new URLSearchParams(location.search).get("page")) || 1
    );
    const [pageSize, setPageSize] = useState(
        () => parseInt(new URLSearchParams(location.search).get("pageSize")) || 10
    );
    const [search, setSearch] = useState(
        () => new URLSearchParams(location.search).get("search") || ""
    );

    const findPermissionNodeByPath = (tree, path) => {
        if (!Array.isArray(tree) || !path) return null;
        const parts = path.split("/").filter(Boolean);
        let nodes = tree;
        let node = null;

        for (let i = 0; i < parts.length; i++) {
            const segment = `/${parts[i]}`;
            node = nodes?.find((item) => item.key === segment);
            if (!node) return null;
            nodes = node.children;
        }

        return node;
    };

    // STEP 1 — Load params from URL (only once)
    useEffect(() => {
        if (firstLoad.current) {
            const q = new URLSearchParams(location.search);
            const pageParam = parseInt(q.get("page")) || 1;
            const pageSizeParam = parseInt(q.get("pageSize")) || 10;
            const searchParam = q.get("search") || "";
            const vesselParam = q.get("vesselId");

            setPage(pageParam);
            setPageSize(pageSizeParam);
            setSearch(searchParam);
            if (vesselParam) setVesselId(Number(vesselParam));

            firstLoad.current = false;
        }
    }, [location.search]);

    // STEP 2 — Keep URL synced with state
    useEffect(() => {
        if (firstLoad.current) return;
        const q = new URLSearchParams(location.search);

        const currentParams = {
            page: parseInt(q.get("page")) || 1,
            pageSize: parseInt(q.get("pageSize")) || 10,
            search: q.get("search") || "",
            vesselId: q.get("vesselId") ? Number(q.get("vesselId")) : null,
        };

        if (
            currentParams.page !== page ||
            currentParams.pageSize !== pageSize ||
            currentParams.search !== search ||
            currentParams.vesselId !== vesselId
        ) {
            q.set("page", page);
            q.set("pageSize", pageSize);
            if (search) q.set("search", search);
            else q.delete("search");
            if (vesselId) q.set("vesselId", vesselId);
            else q.delete("vesselId");
            navigate({ search: q.toString() }, { replace: true });
        }
    }, [page, pageSize, search, vesselId]);

    // STEP 3 — Reset page when vessel changes
    useEffect(() => {
        if (prevVesselRef.current && prevVesselRef.current !== vesselId) {
            setPage(1);
            const q = new URLSearchParams(location.search);
            q.set("page", 1);
            if (vesselId) q.set("vesselId", vesselId);
            else q.delete("vesselId");
            navigate({ search: q.toString() }, { replace: true });
        }
        prevVesselRef.current = vesselId;
    }, [vesselId]);

    // STEP 4 — Fetch vessels and set default one if not present in URL
    const getOptionLocation = async () => {
        if (!permission?.permission_json) return;

        dispatch({ type: "SET_LOADING", loading: true });
        const vessels = await getVessels();

        if (vessels.error) {
            Swal.fire({ icon: "error", title: "Oops...", text: vessels.error });
        } else {
            const currentPath = window?.location?.pathname;
            const permissionNode = findPermissionNodeByPath(permission?.permission_json, currentPath);
            const hasVesselLevelPermissions =
                Array.isArray(permissionNode?.children) && permissionNode.children.length > 0;

            const vesselData = vessels.filter((item) =>
                hasVesselLevelPermissions
                    ? checkPermission(
                          permission,
                          "read_per",
                          "Packages",
                          currentPath + `/${item.name.toLowerCase().replaceAll(" ", "-")}`
                      )
                    : checkPermission(permission, "read_per", "Packages", currentPath)
            );

            const q = new URLSearchParams(location.search);
            const urlVesselId = q.get("vesselId");
            const parsedUrlVesselId = urlVesselId ? Number(urlVesselId) : null;
            const hasAllowedUrlVessel =
                parsedUrlVesselId &&
                vesselData.some((item) => item.id === parsedUrlVesselId);
            const resolvedVesselId = hasAllowedUrlVessel
                ? parsedUrlVesselId
                : vesselData?.[0]?.id ?? null;

            setVesselOptions(
                vesselData.map((item) => ({
                    title: item.name,
                    value: item.id,
                }))
            );

            if (resolvedVesselId) {
                setVesselId(resolvedVesselId);
            } else {
                setVesselId(null);
                setRows([]);
                setCount(0);
            }

            if (!hasAllowedUrlVessel && resolvedVesselId) {
                q.set("vesselId", resolvedVesselId);
                q.set("page", 1);
                q.set("pageSize", pageSize);
                if (search) q.set("search", search);
                else q.delete("search");

                navigate({ search: q.toString() }, { replace: true });
            }
        }

        dispatch({ type: "SET_LOADING", loading: false });
    };

    useEffect(() => {
        getOptionLocation();
    }, [permission]);

    // STEP 5 — Fetch packages data
    const getPackages = async (query) => {
        try {
            if (!query?.vesselId || isNaN(query.vesselId)) {
                throw new Error("vesselId is required and must be an integer");
            }

            const res = await Api.get("/ship-itineraries/parent-sailings", {
                params: query,
            });

            return res?.data || {};
        } catch (e) {
            console.error("Error fetching packages:", e);
            return {
                error:
                    e?.response?.data?.message?.[0] ||
                    e?.message ||
                    "Something went wrong while fetching packages.",
            };
        }
    };

    const getRowsForVessel = async (vesselId, page, pageSize) => {
        setLoading(true);
        const response = await getPackages({
            page,
            limit: pageSize,
            vesselId: Number(vesselId),
            search,
        });
        setLoading(false);

        if (response?.error || !response?.status) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: response?.message || response?.error,
            });
            return;
        }

        const allPackages = response?.data?.packageList || [];
        const total = response?.data?.total || 0;

        setRows(allPackages);
        setCount(total);
    };

    // STEP 6 — Fetch rows whenever vessel/page/pageSize/search changes
    useEffect(() => {
        if (vesselId) getRowsForVessel(vesselId, page, pageSize);
    }, [vesselId, page, pageSize, search]);

    // STEP 7 — Handlers
    const handleTabChange = (val) => {
        setVesselId(val);
        const q = new URLSearchParams(location.search);
        q.set("vesselId", val);
        navigate({ search: q.toString() }, { replace: true });
    };

    const handlePageChange = (newPage) => setPage(newPage);
    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setPage(1);
    };

    const handleEdit = (row) => {
        if (checkPermission(permission, "update_per", "packages", window?.location?.pathname)) {
            const q = new URLSearchParams(location.search);
            q.set("page", page);
            q.set("pageSize", pageSize);
            if (vesselId) q.set("vesselId", vesselId);
            if (search) q.set("search", search);

            navigate(`/shore/mobile-masters/packages/edit/${row?.packageId}?${q.toString()}`);
        } else {
            Swal.fire({ icon: "error", title: "Permission", text: "Update permission missing" });
        }
    };

    // STEP 8 — Table columns
    useEffect(() => {
        setColumns([
            {
                key: "sl",
                title: "Sl",
                align: "left",
                render: (_, __, index) => (page - 1) * pageSize + index + 1,
            },
            {
                key: "itinerary_id",
                title: "Itinerary Id",
                align: "left",
                render: (_, row) => row.package?.itinerary_id || "-",
            },
            {
                key: "title",
                title: "Title",
                align: "left",
                render: (_, row) => row.package?.package_name || "-",
            },
            {
                key: "packageId",
                title: "Cruise Id",
                align: "left",
            },
            {
                key: "sailingDate",
                title: "Sailing Date",
                align: "left",
                render: (_, row) => row.sailingDate || "-",
            },
            {
                key: "actions",
                title: "Actions",
                align: "left",
                render: (_, row) => (
                    <IconButton color="primary" onClick={() => handleEdit(row)}>
                        <EditIcon />
                    </IconButton>
                ),
            },
        ]);
    }, [page, pageSize]);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
                <CircularProgress />
            </Box>
        );
    }

     return (
        <Layout type={"read_per"} module_data={"Packages"}>
            <HeaderBread navigation={navigation} title={"Itinerary"} />

            {/* SwitchBar for Package List and History Logs */}
            <SwitchBar
                tabs={[
                    { title: "Itinerary List", value: "listing" },
                    { value: "logs", title: "History Logs" },
                ]}
                activeTab={active}
                setActiveTab={setActive}
            />

            
            {active === "listing" && (
                <Box className="main_box">
                    <div className="card">
                        <div className="card-header two_item">
                            <h5 className="card-title">Itinerary List</h5>
                            {/* <div className="two_item">
                                <Link
                                    to={`/shore/mobile-masters/packages/add${location.search}`}
                                    className="btn add_v_btn mx-2"
                                >
                                    Add Package
                                </Link>
                            </div> */}
                        </div>
                        <div className="card-body">
                            <Box className="table_container" mt={0}>
                                <div className="table_holder">
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "flex-end",
                                            justifyContent: "space-between",
                                        }}
                                    >
                                        <SwitchBar
                                            tabs={vesselOptions}
                                            activeTab={vesselId}
                                            setActiveTab={handleTabChange}
                                            module="packages-tab"
                                        />
                                    </div>
                                    <CustomizedTable
                                        columns={columns}
                                        rows={rows}
                                        page={page}
                                        setPage={handlePageChange}
                                        count={count}
                                        pageSize={pageSize}
                                        setPageSize={handlePageSizeChange}
                                        title="Itinerary Page"
                                    />
                                </div>
                            </Box>
                        </div>
                    </div>
                </Box>
            )}

            {active === "logs" && (
                <Logs
                    type={"Packages"}
                    slug={"/shore/mobile-masters/packages/edit/"}
                />
            )}

        </Layout>
    );
};
export default ItineraryPackages;
